/**
 * Custom error classes for browser automation and profiles.
 *
 * @example
 * ```typescript
 * try {
 *   await morph.browser.profiles.createProfile({ name: '', repoId: 'owner/repo' });
 * } catch (e) {
 *   if (e instanceof MorphValidationError) {
 *     console.log('Validation failed:', e.field, e.message);
 *   } else if (e instanceof MorphAPIError) {
 *     console.log('API error:', e.code, e.statusCode);
 *   }
 * }
 * ```
 */

/**
 * Error codes for Morph Browser SDK
 */
export type MorphErrorCode =
  // Validation errors
  | 'validation_error'
  | 'invalid_parameter'
  | 'missing_required_field'
  // Authentication errors
  | 'authentication_required'
  | 'invalid_api_key'
  | 'insufficient_permissions'
  // Resource errors
  | 'profile_not_found'
  | 'session_not_found'
  | 'resource_not_found'
  // Limit errors
  | 'profile_limit_exceeded'
  | 'rate_limit_exceeded'
  // Session errors
  | 'session_expired'
  | 'session_save_failed'
  // Network errors
  | 'network_error'
  | 'timeout'
  | 'service_unavailable';

/**
 * Base error class for all Morph SDK errors.
 */
export class MorphError extends Error {
  /** Error code for programmatic handling */
  readonly code: MorphErrorCode;
  /** Original cause of the error, if any */
  readonly cause?: Error;

  constructor(message: string, code: MorphErrorCode, cause?: Error) {
    super(message);
    this.name = 'MorphError';
    this.code = code;
    this.cause = cause;

    // Maintains proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Returns a JSON representation of the error for logging.
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      cause: this.cause?.message,
    };
  }
}

/**
 * Error thrown when API request validation fails.
 */
export class MorphValidationError extends MorphError {
  /** The field that failed validation */
  readonly field?: string;

  constructor(message: string, field?: string) {
    super(message, 'validation_error');
    this.name = 'MorphValidationError';
    this.field = field;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      field: this.field,
    };
  }
}

/**
 * Error thrown when an API request fails.
 */
export class MorphAPIError extends MorphError {
  /** HTTP status code */
  readonly statusCode: number;
  /** Request ID for debugging (if available) */
  readonly requestId?: string;
  /** Raw response body */
  readonly rawResponse?: string;

  constructor(
    message: string,
    code: MorphErrorCode,
    statusCode: number,
    options?: {
      requestId?: string;
      rawResponse?: string;
      cause?: Error;
    }
  ) {
    super(message, code, options?.cause);
    this.name = 'MorphAPIError';
    this.statusCode = statusCode;
    this.requestId = options?.requestId;
    this.rawResponse = options?.rawResponse;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      statusCode: this.statusCode,
      requestId: this.requestId,
    };
  }
}

/**
 * Error thrown when authentication fails.
 */
export class MorphAuthenticationError extends MorphAPIError {
  constructor(message: string = 'Authentication required. Please provide a valid API key.') {
    super(message, 'authentication_required', 401);
    this.name = 'MorphAuthenticationError';
  }
}

/**
 * Error thrown when rate limit is exceeded.
 */
export class MorphRateLimitError extends MorphAPIError {
  /** When the rate limit resets (Unix timestamp) */
  readonly resetAt?: number;
  /** Number of seconds until reset */
  readonly retryAfter?: number;

  constructor(
    message: string = 'Rate limit exceeded. Please retry later.',
    options?: {
      resetAt?: number;
      retryAfter?: number;
      requestId?: string;
    }
  ) {
    super(message, 'rate_limit_exceeded', 429, { requestId: options?.requestId });
    this.name = 'MorphRateLimitError';
    this.resetAt = options?.resetAt;
    this.retryAfter = options?.retryAfter;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      resetAt: this.resetAt,
      retryAfter: this.retryAfter,
    };
  }
}

/**
 * Error thrown when a resource is not found.
 */
export class MorphNotFoundError extends MorphAPIError {
  /** The type of resource that was not found */
  readonly resourceType: string;
  /** The ID of the resource that was not found */
  readonly resourceId?: string;

  constructor(resourceType: string, resourceId?: string) {
    const message = resourceId
      ? `${resourceType} '${resourceId}' not found`
      : `${resourceType} not found`;
    super(message, 'resource_not_found', 404);
    this.name = 'MorphNotFoundError';
    this.resourceType = resourceType;
    this.resourceId = resourceId;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      resourceType: this.resourceType,
      resourceId: this.resourceId,
    };
  }
}

/**
 * Error thrown when profile limit is exceeded.
 */
export class MorphProfileLimitError extends MorphAPIError {
  /** Current number of profiles */
  readonly currentCount?: number;
  /** Maximum allowed profiles for the plan */
  readonly maxAllowed?: number;

  constructor(
    message: string = 'Profile limit exceeded for your plan.',
    options?: {
      currentCount?: number;
      maxAllowed?: number;
      requestId?: string;
    }
  ) {
    super(message, 'profile_limit_exceeded', 403, { requestId: options?.requestId });
    this.name = 'MorphProfileLimitError';
    this.currentCount = options?.currentCount;
    this.maxAllowed = options?.maxAllowed;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      currentCount: this.currentCount,
      maxAllowed: this.maxAllowed,
    };
  }
}

/**
 * Parse an API error response and return the appropriate error class.
 */
export function parseAPIError(
  statusCode: number,
  responseText: string,
  requestId?: string
): MorphAPIError {
  // Try to parse JSON error response
  let errorData: { detail?: string; code?: string; message?: string } = {};
  try {
    errorData = JSON.parse(responseText);
  } catch {
    // Not JSON, use raw text
  }

  const message = errorData.detail || errorData.message || responseText || 'Unknown error';
  const code = errorData.code;

  // Map status codes to specific error classes
  switch (statusCode) {
    case 401:
      return new MorphAuthenticationError(message);

    case 403:
      if (code === 'profile_limit_exceeded' || message.toLowerCase().includes('limit')) {
        return new MorphProfileLimitError(message, { requestId });
      }
      return new MorphAPIError(message, 'insufficient_permissions', statusCode, { requestId, rawResponse: responseText });

    case 404:
      if (message.toLowerCase().includes('profile')) {
        return new MorphNotFoundError('Profile', undefined);
      }
      if (message.toLowerCase().includes('session')) {
        return new MorphNotFoundError('Session', undefined);
      }
      return new MorphAPIError(message, 'resource_not_found', statusCode, { requestId, rawResponse: responseText });

    case 429:
      return new MorphRateLimitError(message, { requestId });

    case 422:
      return new MorphAPIError(message, 'validation_error', statusCode, { requestId, rawResponse: responseText });

    case 500:
    case 502:
    case 503:
    case 504:
      return new MorphAPIError(message, 'service_unavailable', statusCode, { requestId, rawResponse: responseText });

    default:
      return new MorphAPIError(message, 'network_error', statusCode, { requestId, rawResponse: responseText });
  }
}
